import { attach, combine, Effect, forward, guard, sample } from 'effector';
import { routes } from 'features/fallback/routes';
import { jwtDecode } from 'libs/jwt-decode';
import { navigate } from 'libs/router';
import { baseUrls$ } from 'model/config.model';
import { SessionTokenPayload } from 'model/domain';
import { ErrorType } from 'model/error.model';
import { captureErrorFx } from 'model/helpers/analytics.model';
import { RootDomain } from 'model/root.model';
import { sessionToken$, applicationKey$, sessionConfig$ } from 'model/session.model';
import { localizationOptions$ } from 'model/localization.model';
import { ConnectSocketPayload, createSocket } from 'model/helpers/socket';
import { version } from '../../../package.json';

const socket = createSocket();

export function createLinkFactory(effect: Effect<{ value: string, sessionId: string }, any>) {
	const changeIsSuccess = RootDomain.event<boolean | null>();
	const changeValue = RootDomain.event<string>();
	const useDifferent = RootDomain.event();
	const tryAgain = RootDomain.event();

	const isSuccess$ = RootDomain.store(null);
	const value$ = RootDomain.store('');

	const sendFx = attach({
		effect,
		source: { value: value$, session: sessionConfig$ },
		mapParams: (value: string, source) => ({
			value: value,
			sessionId: source.session.id,
		}),
	});

	isSuccess$
		.on(changeIsSuccess, (_, value) => value)
		.on(sendFx.done, () => true)
		.on(sendFx.fail, () => false)
		.on(tryAgain, () => null)
		.reset(useDifferent);

	value$
		.on(changeValue, (_, value) => value)
		.on(sendFx.done, (_, { params }) => params)
		.reset(useDifferent);

	return {
		sendFx,
		tryAgain,
		changeValue,
		useDifferent,
		changeIsSuccess,
		value$,
		isSuccess$,
	};
}

export const isFallbackExit$ = RootDomain.store<boolean>(false);

export const openExitPopup = RootDomain.event();
export const closeExitPopup = RootDomain.event();
export const fallbackCancel = RootDomain.event<ErrorType | null>();
export const connectFallbackSocket = RootDomain.event();
export const disconnectFallbackSocket = RootDomain.event();
export const onFallbackFinish = RootDomain.event();
const onFallbackConnected = RootDomain.event();
const onMobileConnected = RootDomain.event<{ error?: string }>();

export const room$ = sessionToken$.map(token => {
	if (!token) { return null; }

	try {
		const payload: SessionTokenPayload = jwtDecode(token);

		return payload.room || null;
	} catch (e) {
		captureErrorFx(e);
		return null;
	}
});
export const link$ = combine(room$, sessionConfig$, (room, config) => {
	if (!room) { return null; }

	return config ? `${config.urls.handoff_url}#${room}` : '';
});

isFallbackExit$
	.on(openExitPopup, () => true)
	.on(closeExitPopup, () => false);

socket.on('connect', onFallbackConnected);
socket.on('joined[mobile]', onMobileConnected);
socket.on('canceled[mobile]', fallbackCancel.prepend((body: { errorType: ErrorType }) => {
	return body.errorType || null;
}));
socket.on('finish', onFallbackFinish);

guard({
	source: sample({
		source: { token: sessionToken$, baseUrls: baseUrls$ },
		clock: connectFallbackSocket,
		fn: ({ token, baseUrls }): ConnectSocketPayload => ({
			baseUrl: baseUrls.apiBaseUrl,
			bearer: token,
		}),
	}),
	filter: sessionToken$.map(token => token !== null),
	target: socket.connect,
});
forward({
	from: onFallbackConnected,
	to: socket.emit.prepend(() => ({ event: 'join[desktop]', body: { version } })),
});
sample({
	source: { config: sessionConfig$, room: room$ },
	clock: fallbackCancel,
	fn: ({ config, room }) => {
		return {
			event: 'canceled[desktop]',
			body: { sessionId: config?.id, room },
		};
	},
	target: socket.emit,
});

forward({
	from: sample({
		source: {
			token: sessionToken$,
			applicationKey: applicationKey$,
			localizationOptions: localizationOptions$,
		},
		clock: onMobileConnected,
		fn: (body) => ({
			event: 'params[desktop]',
			body: {
				token: body.token,
				applicationKey: body.applicationKey,
				localizationOptions: {
					language: body.localizationOptions.language,
					languageSelector: body.localizationOptions.languageSelector,
				},
			},
		}),
	}),
	to: [
		socket.emit,
	],
});
guard({
	source: onMobileConnected,
	filter: (body) => body.error === null || body.error === undefined,
	target: navigate.prepend(() => routes.connected),
});
forward({
	from: disconnectFallbackSocket,
	to: socket.disconnect,
});
